import { signalsTarget } from './signals-target';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { SignalsWorkflowConfig } from './settings/workflow-builder/workflow-builder.models';

export interface KlaviyoIntegrationConfig {
  target?: string;
  hasPrivateApiKey?: boolean;
  hasOAuthToken?: boolean;
  oauthConnectedAt?: string | null;
  enabled?: boolean;
}

export interface EmarsysIntegrationConfig {
  target?: string;
  clientId?: string | null;
  hasClientSecret?: boolean;
  enabled?: boolean;
}

export interface EmarsysCredentialsPayload {
  clientId: string;
  clientSecret: string;
}

export interface MondayGroup {
  id: string;
  title: string;
  key?: string;
}

export interface MondayColumn {
  id: string;
  title: string;
  type?: string;
}

export interface MondayBoard {
  id: string;
  name: string;
  url?: string;
  groups?: MondayGroup[];
  columns?: MondayColumn[];
}

export interface MondayConnection {
  id?: string;
  target?: string;
  monday_account_id?: string;
  monday_user_id?: string;
  access_token?: string | null;
  board_id?: string;
  group_id?: string;
  groups?: MondayGroup[];
  column_mapping?: Record<string, string>;
  status?: string;
  connected?: boolean;
  message?: string;
  created_at?: string;
  updated_at?: string;
  has_access_token?: boolean;
  column_types?: string[];
}

export interface MondayWorkspace {
  id: string;
  name: string;
  kind?: string;
}

export interface MondayBoardKindOption {
  value: string;
  label: string;
}

export interface MondayBoardsResponse {
  boards?: MondayBoard[];
}

export interface MondayBoardMetadataResponse {
  board?: MondayBoard;
}

export interface MondayWorkspacesResponse {
  workspaces?: MondayWorkspace[];
  boardKindOptions?: MondayBoardKindOption[];
}

export interface MondayConnectionPayload {
  target: string;
  board_id: string;
  group_id: string;
  save_connection: boolean;
}

export interface MondayLeadsSetupResponse {
  board?: MondayBoard;
  groups?: MondayGroup[];
  columns?: MondayColumn[];
  created_columns?: MondayColumn[];
  column_mapping?: Record<string, string>;
  connection?: MondayConnection;
}

export interface MondayDefaultSignalsBoardPayload {
  target: string;
  workspace_id?: string;
  board_kind?: string;
  description: string;
  save_connection: boolean;
}

export interface MondayDefaultSignalsBoardResponse extends MondayLeadsSetupResponse {}

export interface RealtimeSignalOutputField {
  key: string;
  label?: string | null;
  description: string;
}

export interface RealtimeSignalsDashboardConfig {
  target: string;
  enabled: boolean;
  output_fields: RealtimeSignalOutputField[];
  extra_prompt_instructions: string;
}

export interface LinkedinConversion {
  id: string;
  name: string;
}

export interface LinkedinRealTimeSignalsConfig {
  target: string;
  partnerId: string;
  conversions: LinkedinConversion[];
}

export interface BehavioralIntelligenceChannel {
  threshold?: number;
  value: string;
  label: string;
  connected: boolean;
  /** The saved workflow's channel settings, when the API includes them. */
  settings?: { eventName?: string; column_name?: string };
}

export interface BehavioralIntelligenceChannelsResponse {
  target: string;
  channels: BehavioralIntelligenceChannel[];
}

/**
 * `unavailable`: the figures could not be worked out for these filters (e.g.
 * a visit type the data cannot split) — they come back null, never zero.
 */
export type SegmentMetricsStatus = 'available' | 'unavailable';

export interface BehavioralIntelligenceSegment {
  label: string;
  value?: string;
  source?: string;
  canDelete?: boolean;
  desc?: string;
  /** null when not available for the filters — never read it as zero */
  sessions?: number | string | null;
  percentage: number | string | null;
  totalRevenue?: number | string | null;
  metricsStatus?: SegmentMetricsStatus;
  /** Channels this segment is wired to; same shape as the channels endpoint. */
  channels?: BehavioralIntelligenceChannel[];
  channelsCount?: number;
}

export type SegmentReportingMode = 'offline' | 'realtime' | 'disabled';
export type SegmentReportingSourceMode = Exclude<SegmentReportingMode, 'disabled'>;
export type SegmentReportingStatus =
  | 'initializing'
  | 'partial'
  | 'ready'
  | 'error'
  | 'unavailable';

export interface SegmentReportingPeriod {
  start: string;
  end: string;
  revision?: number;
}

/**
 * One collection source's figures for the requested range. `null` means the
 * figure is not available yet - never zero.
 */
export interface SegmentReportingSource {
  mode: SegmentReportingSourceMode;
  status: SegmentReportingStatus;
  sessions: number | null;
  totalRevenue: number | null;
  lastUpdatedAt: string | null;
  periods: SegmentReportingPeriod[];
}

/** Custom stages: the backend owns the collection mode and its transitions. */
export interface SegmentReporting {
  mode: SegmentReportingMode;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  status: SegmentReportingStatus;
  lastUpdatedAt: string | null;
  sources: SegmentReportingSource[];
}

export type SegmentsDevice = 'desktop' | 'mobile' | 'tablet';

/** New or returning visits; omitted for all visits. */
export type SegmentsVisitType = 'newV' | 'retV';

/** The connected channels `/behavioral-intelligence/segments` can filter by. */
export const SEGMENTS_CHANNELS = [
  'meta',
  'email',
  'google_ads',
  'monday_crm',
  'klaviyo',
  'emarsys',
  'linkedin_realtime_signals',
] as const;

export type SegmentsChannel = (typeof SEGMENTS_CHANNELS)[number];

export function isSegmentsChannel(value: string): value is SegmentsChannel {
  return (SEGMENTS_CHANNELS as readonly string[]).includes(value);
}

/** Totals across all processed traffic, sent on the purchase journey stage. */
export interface SegmentsRollupTotals {
  scope: string;
  totalProcessedSessions: number | string | null;
  totalRevenue: number | string | null;
}

export interface BehavioralIntelligenceSegmentsResponse {
  selectedStage: string;
  label: string;
  value: string;
  /** Custom stages only: the key the `/config/stage` endpoints expect. */
  key?: string;
  desc?: string;
  description?: string;
  source?: string;
  canDelete?: boolean;
  threshold?: number | string;
  userDetails?: { name?: string; email?: string };
  /** Stage-level totals; when missing they are summed from `items`. */
  sessions?: number | string | null;
  totalRevenue?: number | string | null;
  metricsStatus?: SegmentMetricsStatus;
  /** Purchase journey stage only: totals for all traffic. */
  rollupTotals?: SegmentsRollupTotals;
  items: BehavioralIntelligenceSegment[];
  /** Set on custom stages, which carry no `items` of their own. */
  channels?: BehavioralIntelligenceChannel[];
  channelsCount?: number;
  /** Custom stages only. */
  reporting?: SegmentReporting;
}

export interface BehavioralIntelligenceStagePayload {
  target: string;
  label: string;
  description: string;
  threshold: number;
}

export interface UpdateBehavioralIntelligenceStagePayload extends BehavioralIntelligenceStagePayload {
  key: string;
}

export interface DeleteBehavioralIntelligenceStagePayload {
  target: string;
  key: string;
}

export interface SingleWorkflowBuilderPayload {
  target: string;
  stage: string;
  /** `null` for custom stages, which have no sub-segment. */
  segment: string | null;
  workflow: {
    id: string;
    event: { key: string; name: string; description: string };
    rule: { operator: string; threshold: number };
    channel: {
      type: string;
      enabled: boolean;
      settings: { integrationId: string; eventName: string };
    };
  };
}

export interface WorkflowEstimatedReachPayload {
  target: string;
  numberOfSessions: number;
  description: string;
  bucketSize: number;
}

export interface WorkflowEstimatedReachBucket {
  scorePercent: number;
  sessionCount: number;
  sessionPercent: number;
  estimatedSessionCount: number;
}

export interface WorkflowEstimatedReachResult {
  provider?: string;
  model?: string;
  status?: string;
  distribution?: WorkflowEstimatedReachBucket[];
}

export interface WorkflowEstimatedReachResponse {
  requestId?: string;
  target?: string;
  totalSessions?: number;
  /** Identified users behind `totalSessions`, for the whole date range. */
  identifiedUsers?: number;
  selectedSessions?: number;
  bucketSize?: number;
  cached?: boolean;
  results?: WorkflowEstimatedReachResult[];
}

export interface NudgeRecommendationPayload {
  /** The stage, which this endpoint calls the segment. */
  segment: string;
  segmentDescription: string;
  /** Empty for custom stages, which have no sub-segments. */
  subsegment: string;
  subsegmentDescription: string;
  /** Fixed by the caller - not user input. */
  description: string;
}

export interface NudgeRecommendation {
  /** Stable ID in the shared database catalog. */
  libraryId?: string;
  name?: string;
  incentiveId?: string;
  family?: string;
  description?: string;
  score?: number;
  scorePercent?: number;
  /** Shop the screenshot was taken on, and the device it was taken with. */
  sourceTarget?: string;
  platform?: string;
}

export interface NudgesRecommendationResponse {
  recommendedNudgeDetails?: NudgeRecommendation[];
}

export interface DeleteSingleWorkflowBuilderPayload {
  target: string;
  stage: string;
  segment?: string;
  channel: string;
}

export interface AddBehavioralIntelligenceSegmentPayload {
  target: string;
  selectedStage: string;
  label: string;
  description: string;
}

export interface DeleteBehavioralIntelligenceSegmentPayload {
  target: string;
  selectedStage: string;
  label: string;
}

@Injectable({
  providedIn: 'root',
})
export class InsightsSignalsService {
  constructor(private _http: HttpClient) {}

  private buildQueryString(params: any): string {
    return Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&');
  }

  private normalizeTarget(target: string): string {
    target = signalsTarget(target);
    return target === 'behamicsSample' ? 'behamicsSampleSignals' : target;
  }

  addTargetToSignals(
    target: string,
    addToIntent: boolean = false,
  ): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.post<any>(`/recorder/add-target-to-signals`, {
      target,
      addToIntent,
    });
  }

  getBehavioralIntelligenceTargets(target?: string): Observable<any> {
    target = this.normalizeTarget(target || '');

    const params = target ? `?target=${encodeURIComponent(target)}` : '';

    return this._http.get<any>(`/get-behavioral-intelligence-targets${params}`);
  }

  addTargetToBehavioralIntelligence(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.post<any>(
      `/recorder/add-target-to-behavioral-intelligence`,
      { target },
    );
  }

  getAllTargetInSignals(): Observable<any> {
    return this._http.get<any>(`/recorder/get-all-targets-in-signals`);
  }

  removeTargetFromSignals(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.delete<any>(
      `/recorder/remove-target-in-signals?target=${target}`,
    );
  }

  getTargetPromptInSignals(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.get<any>(
      `/recorder/get-target-prompt-in-signals?target=${target}`,
    );
  }

  editTargetPromptInSignals(data: any): Observable<any> {
    data['target'] = this.normalizeTarget(data['target']);
    return this._http.post<any>(
      `/recorder/edit-target-prompt-in-signals`,
      data,
    );
  }

  testNewPromptWithAI(data: any): Observable<any> {
    data['target'] = this.normalizeTarget(data['target']);
    return this._http.post<any>(`/recorder/test-new-prompt-with-ai`, data);
  }

  updateTargetInactivityTime(data: any): Observable<any> {
    data['target'] = this.normalizeTarget(data['target']);
    return this._http.post<any>(
      `/recorder/update-target-inactivity-time`,
      data,
    );
  }

  getMetaCapiConfig(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.get<any>(`/recorder/meta-capi-config?target=${target}`);
  }

  updateMetaCapiConfig(data: any): Observable<any> {
    data['target'] = this.normalizeTarget(data['target']);
    return this._http.post<any>(`/recorder/meta-capi-config`, data);
  }

  getKlaviyoConfig(target: string): Observable<KlaviyoIntegrationConfig> {
    target = this.normalizeTarget(target);
    return this._http.get<KlaviyoIntegrationConfig>(
      `/recorder/klaviyo-config?target=${target}`,
    );
  }

  updateKlaviyoConfig(
    target: string,
    data: {
      enabled: boolean;
    },
  ): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.post<any>(
      `/recorder/klaviyo-config?target=${target}`,
      data,
    );
  }

  getKlaviyoOAuthConnectUrl(
    returnUrl: string,
    shopID: string,
  ): Observable<{ authUrl: string }> {
    return this._http.get<{ authUrl: string }>(
      `/recorder/klaviyo/oauth/connect?returnUrl=${encodeURIComponent(returnUrl)}&shopID=${shopID}`,
    );
  }

  disconnectKlaviyoOAuth(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.delete<any>(
      `/recorder/klaviyo/oauth/disconnect?target=${target}`,
    );
  }

  getEmarsysConfig(target: string): Observable<EmarsysIntegrationConfig> {
    target = this.normalizeTarget(target);
    return this._http.get<EmarsysIntegrationConfig>(
      `/emarsys-config?target=${encodeURIComponent(target)}`,
    );
  }

  updateEmarsysConfig(
    target: string,
    data: EmarsysCredentialsPayload,
  ): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.post<any>(
      `/emarsys-config?target=${encodeURIComponent(target)}`,
      data,
    );
  }

  getHubspotConnectUrl(
    target: string,
  ): Observable<{ success: boolean; authorizationUrl: string }> {
    target = this.normalizeTarget(target);
    return this._http.get<{ success: boolean; authorizationUrl: string }>(
      `/hubspot/connect?target=${encodeURIComponent(target)}`,
    );
  }

  /** Exchanges the OAuth `code`/`state` HubSpot redirected back with. */
  handleHubspotOAuthCallback(
    params: Record<string, string>,
  ): Observable<{ success: boolean; target: string; message?: string }> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        queryParams.set(key, value);
      }
    });

    return this._http.get<{ success: boolean; target: string; message?: string }>(
      `/hubspot?${queryParams.toString()}`,
    );
  }

  disconnectHubspot(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.delete<any>(
      `/hubspot/disconnect?target=${encodeURIComponent(target)}`,
    );
  }

  /**
   * HubSpot has no config endpoint; its connection state comes from the
   * behavioral-intelligence channels list.
   */
  getHubspotConnected(target: string): Observable<boolean> {
    return this.getBehavioralIntelligenceChannels(
      this.normalizeTarget(target),
    ).pipe(
      map(
        (response) =>
          !!response?.channels?.find((channel) => channel.value === 'hubspot')
            ?.connected,
      ),
    );
  }

  getMondayConnection(target: string): Observable<MondayConnection> {
    return this._http.get<MondayConnection>(
      `/monday/connection?target=${encodeURIComponent(target)}`,
    );
  }

  getMondayOAuthConnectUrl(target: string): Observable<{ authUrl: string }> {
    return this._http.get<{ authUrl: string }>(
      `/monday/oauth/connect?target=${encodeURIComponent(target)}`,
    );
  }

  handleMondayOAuthCallback(params: Record<string, string>): Observable<any> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, value);
      }
    });

    return this._http.get(`/monday/oauth/callback?${queryParams.toString()}`, {
      responseType: 'text',
    });
  }

  getMondayBoards(target: string): Observable<MondayBoardsResponse> {
    return this._http.get<MondayBoardsResponse>(
      `/monday/boards?target=${encodeURIComponent(target)}`,
    );
  }

  getMondayBoardMetadata(
    target: string,
    boardId: string,
  ): Observable<MondayBoardMetadataResponse> {
    return this._http.get<MondayBoardMetadataResponse>(
      `/monday/boards/${encodeURIComponent(boardId)}/metadata?target=${encodeURIComponent(target)}`,
    );
  }

  saveMondayConnection(data: MondayConnectionPayload): Observable<any> {
    return this._http.post<any>(`/monday/connection`, data);
  }

  disconnectMondayConnection(target: string): Observable<any> {
    return this._http.delete<any>(
      `/monday/connection?target=${encodeURIComponent(target)}`,
    );
  }

  setupMondayLeadsBoard(
    data: MondayConnectionPayload,
  ): Observable<MondayLeadsSetupResponse> {
    return this._http.post<MondayLeadsSetupResponse>(
      `/monday/boards/leads/setup`,
      data,
    );
  }

  getMondayWorkspaces(target: string): Observable<MondayWorkspacesResponse> {
    return this._http.get<MondayWorkspacesResponse>(
      `/monday/workspaces?target=${encodeURIComponent(target)}`,
    );
  }

  createDefaultMondaySignalsBoard(
    data: MondayDefaultSignalsBoardPayload,
  ): Observable<MondayDefaultSignalsBoardResponse> {
    return this._http.post<MondayDefaultSignalsBoardResponse>(
      `/monday/boards/default-signals`,
      data,
    );
  }

  getGoogleAdsConfig(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.get<any>(`/google-ads/config?target=${target}`);
  }

  updateGoogleAdsConfig(data: any): Observable<any> {
    const normalizedTarget = this.normalizeTarget(data['target']);
    const { refreshToken, ...payload } = data || {};

    return this._http.post<any>(`/google-ads/config`, {
      ...payload,
      target: normalizedTarget,
    });
  }

  getGoogleAdsConnectUrl(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.get(`/google-ads/connect?target=${target}`);
  }

  getGoogleAdsConnectEndpoint(target: string): string {
    target = this.normalizeTarget(target);
    return `/google-ads/connect?target=${encodeURIComponent(target)}`;
  }

  handleGoogleAdsCallback(
    target: string,
    params: Record<string, string>,
  ): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.set('target', this.normalizeTarget(target));

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, value);
      }
    });

    return this._http.get<any>(
      `/google-ads/callback?${queryParams.toString()}`,
    );
  }

  getGoogleAdsConversionActions(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.get<any>(
      `/google-ads/conversion-actions?target=${target}`,
    );
  }

  createGoogleAdsConversionAction(data: any): Observable<any> {
    data['target'] = this.normalizeTarget(data['target']);
    return this._http.post<any>(`/google-ads/conversion-actions`, data);
  }

  createGoogleAdsCustomVariable(data: {
    target: string;
    name: string;
    tag: string;
  }): Observable<any> {
    return this._http.post<any>(`/google-ads/custom-variables`, {
      ...data,
      target: this.normalizeTarget(data.target),
    });
  }

  removeGoogleAdsConversionAction(data: any): Observable<any> {
    data['target'] = this.normalizeTarget(data['target']);
    return this._http.post<any>(`/google-ads/conversion-actions/remove`, data);
  }

  getLinkedinRealTimeSignalsConfig(
    target: string,
  ): Observable<LinkedinRealTimeSignalsConfig> {
    const normalizedTarget = this.normalizeTarget(target);

    return this._http
      .get<any>(
        `/linkedinRealTimeSignals?target=${encodeURIComponent(normalizedTarget)}`,
      )
      .pipe(
        map((response) => {
          const config = response?.data ?? response?.config ?? response ?? {};

          return {
            target: String(config.target ?? normalizedTarget),
            partnerId: String(config.partnerId ?? ''),
            conversions: Array.isArray(config.conversions)
              ? config.conversions.map((conversion: any) => ({
                  id: String(conversion?.id ?? ''),
                  name: String(conversion?.name ?? ''),
                }))
              : [],
          };
        }),
      );
  }

  createLinkedinRealTimeSignalsConfig(
    data: LinkedinRealTimeSignalsConfig,
  ): Observable<any> {
    return this._http.post<any>(`/linkedinRealTimeSignals`, {
      ...data,
      target: this.normalizeTarget(data.target),
      conversions: data.conversions || [],
    });
  }

  addLinkedinConversion(
    target: string,
    conversion: LinkedinConversion,
  ): Observable<any> {
    return this._http.post<any>(`/linkedinRealTimeSignals/conversions`, {
      target: this.normalizeTarget(target),
      conversion,
    });
  }

  deleteLinkedinConversion(
    target: string,
    conversionId: string,
  ): Observable<any> {
    return this._http.delete<any>(
      `/linkedinRealTimeSignals/conversions?target=${encodeURIComponent(this.normalizeTarget(target))}&conversionId=${encodeURIComponent(conversionId)}`,
    );
  }

  deleteLinkedinRealTimeSignalsConfig(target: string): Observable<any> {
    return this._http.delete<any>(
      `/linkedinRealTimeSignals?target=${encodeURIComponent(this.normalizeTarget(target))}`,
    );
  }

  getWorkflowBuilder(target: string): Observable<any> {
    target = this.normalizeTarget(target);
    return this._http.get<any>(`/getWorkflowBuilder?target=${target}`);
  }

  saveWorkflowBuilder(data: {
    target: string;
    data: SignalsWorkflowConfig;
  }): Observable<any> {
    return this._http.post<any>(`/saveWorkflowBuilder`, {
      ...data,
      target: this.normalizeTarget(data.target),
    });
  }

  getRealtimeSignalsDashboard(
    target: string,
  ): Observable<RealtimeSignalsDashboardConfig> {
    target = this.normalizeTarget(target);
    return this._http.get<RealtimeSignalsDashboardConfig>(
      `/realtime-signals-dashboard?target=${target}`,
    );
  }

  updateRealtimeSignalsDashboard(
    data: RealtimeSignalsDashboardConfig,
  ): Observable<any> {
    return this._http.post<any>(`/realtime-signals-dashboard`, {
      ...data,
      target: this.normalizeTarget(data.target),
    });
  }

  getWorkflowBuilderReportDashboard(data: {
    target: string;
    startDate: string;
    endDate: string;
  }): Observable<RealtimeSignalsDashboardConfig> {
    const target = this.normalizeTarget(data.target);
    return this._http.get<RealtimeSignalsDashboardConfig>(
      `/recorder/workflow-builder-report/dashboard?target=${target}&startDate=${data.startDate}&endDate=${data.endDate}`,
    );
  }

  getBehavioralIntelligenceSegments(data: {
    target: string;
    startDate: string;
    endDate: string;
    includeCustoms?: boolean;
    /** Omitted for all devices. */
    device?: SegmentsDevice;
    /**
     * Keeps only segments with an enabled workflow on this connected channel;
     * omitted for all channels.
     */
    channel?: SegmentsChannel;
    /**
     * Filters the same metrics to new or returning visits; omitted for all.
     * New + returning can be less than all: some visits are unclassified.
     */
    visitType?: SegmentsVisitType;
  }): Observable<BehavioralIntelligenceSegmentsResponse[]> {
    const { device, channel, visitType, ...params } = data;
    const query = this.buildQueryString({
      ...params,
      ...(device ? { device } : {}),
      ...(channel ? { channel } : {}),
      ...(visitType ? { visitType } : {}),
    });

    return this._http.get<BehavioralIntelligenceSegmentsResponse[]>(
      `/behavioral-intelligence/segments?${query}`,
    );
  }

  getBehavioralIntelligenceChannels(
    target: string,
  ): Observable<BehavioralIntelligenceChannelsResponse> {
    return this._http.get<BehavioralIntelligenceChannelsResponse>(
      `/behavioral-intelligence/channels?target=${encodeURIComponent(target)}`,
    );
  }

  addBehavioralIntelligenceStage(
    data: BehavioralIntelligenceStagePayload,
  ): Observable<any> {
    return this._http.post<any>(`/behavioral-intelligence/config/stage`, data);
  }

  updateBehavioralIntelligenceStage(
    data: UpdateBehavioralIntelligenceStagePayload,
  ): Observable<any> {
    return this._http.patch<any>(`/behavioral-intelligence/config/stage`, data);
  }

  deleteBehavioralIntelligenceStage(
    data: DeleteBehavioralIntelligenceStagePayload,
  ): Observable<any> {
    return this._http.delete<any>(`/behavioral-intelligence/config/stage`, {
      body: data,
    });
  }

  /**
   * Scores a sample of sessions against a description and returns the reach
   * distribution across score buckets. Slow (~15s) and billed per call, so
   * callers should debounce and de-duplicate.
   */
  getWorkflowEstimatedReachScore(
    data: WorkflowEstimatedReachPayload,
  ): Observable<WorkflowEstimatedReachResponse> {
    return this._http.post<WorkflowEstimatedReachResponse>(
      `/workflow-estimated-reach/score`,
      data,
    );
  }

  /**
   * Nudge patterns recommended for one segment, ranked by fit. The target
   * rides in the query string, the segment in the body.
   */
  getNudgeRecommendations(
    target: string,
    data: NudgeRecommendationPayload,
  ): Observable<NudgesRecommendationResponse> {
    return this._http.post<NudgesRecommendationResponse>(
      `/nudges-recommendation?target=${encodeURIComponent(target)}`,
      data,
    );
  }

  saveSingleWorkflowBuilder(
    data: SingleWorkflowBuilderPayload,
  ): Observable<any> {
    return this._http.post<any>(`/saveSingleWorkflowBuilder`, {
      ...data,
      target: this.normalizeTarget(data.target),
    });
  }

  deleteSingleWorkflowBuilder(
    data: DeleteSingleWorkflowBuilderPayload,
  ): Observable<any> {
    return this._http.delete<any>(`/deleteSingleWorkflowBuilder`, {
      body: { ...data, target: this.normalizeTarget(data.target) },
    });
  }

  addBehavioralIntelligenceSegment(
    data: AddBehavioralIntelligenceSegmentPayload,
  ): Observable<any> {
    return this._http.post<any>(
      `/behavioral-intelligence/config/segment`,
      data,
    );
  }

  deleteBehavioralIntelligenceSegment(
    data: DeleteBehavioralIntelligenceSegmentPayload,
  ): Observable<any> {
    return this._http.delete<any>(`/behavioral-intelligence/config/segment`, {
      body: data,
    });
  }

  getWorkflowBuilderReportEvents(
    params: any,
  ): Observable<RealtimeSignalsDashboardConfig> {
    return this._http.get<RealtimeSignalsDashboardConfig>(
      `/recorder/workflow-builder-report/events?${this.buildQueryString(params)}`,
    );
  }

  getWorkflowBuilderReportMetadata(
    params: any,
  ): Observable<RealtimeSignalsDashboardConfig> {
    return this._http.get<RealtimeSignalsDashboardConfig>(
      `/recorder/workflow-builder-report/metadata?${this.buildQueryString(params)}`,
    );
  }

  getWorkflowBuilderReportSession(
    params: any,
  ): Observable<RealtimeSignalsDashboardConfig> {
    return this._http.get<RealtimeSignalsDashboardConfig>(
      `/recorder/workflow-builder-report/session?${this.buildQueryString(params)}`,
    );
  }

  getSignalsManagementLogs() {
    return this._http.get(`/recorder/signals-management-logs`);
  }
}
